import {
  useCallback, useRef, useState,
} from 'react';

/**
 * ShouldPlayAdHook
 *
 * @typedef {object} ShouldPlayAdHook
 * @property {boolean} shouldPlayAd - Whether an ad should be played now.
 * @property {(videoId: string) => void} registerVideoPlay - Call this when a video is played.
 * @property {() => void} registerAdPlayed - Call this when an ad is shown.
 */

/**
 * Custom hook to determine if an ad should be played based on the number of video plays.
 *
 * @param {object} [params]
 * @param {number} [params.adInterval=7] - The number of video plays between ads.
 * @returns {ShouldPlayAdHook} An object with ad logic state and control functions.
 */
export function useShouldPlayAd({ adInterval = 7 } = {}) {
  const videosPlayedSinceAdRef = useRef(0);
  const [shouldPlayAd, setShouldPlayAd] = useState(false);
  const lastTrackedVideoRef = useRef('');

  const registerVideoPlay = useCallback((/** @type {string} */ videoId) => {
    if (lastTrackedVideoRef.current === videoId) {
      return;
    }
    lastTrackedVideoRef.current = videoId;
    videosPlayedSinceAdRef.current += 1;
    setShouldPlayAd(videosPlayedSinceAdRef.current >= adInterval);
  }, [adInterval]);

  const registerAdPlayed = useCallback(() => {
    videosPlayedSinceAdRef.current = 0;
    setShouldPlayAd(false);
  }, []);

  return {
    shouldPlayAd,
    registerVideoPlay,
    registerAdPlayed,
  };
}
